'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { SEARCH_PARAM_KEYS } from '@/constants/auth'
import { useAppLoaderContext } from '@/context/AppLoaderContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import useCheckAuth from '@/hooks/auth/useCheckAuth'
import useHash from '@/hooks/useHash'
import { getLoginUrl, getLogoutUrl, LoginParams } from '@/lib/url'

type AuthContextType = {
  isSignedIn: boolean
  isCheckingAuth: boolean
  handleLogin: (params?: LoginParams) => void
  handleLogout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false)

  const hash = useHash()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const appState = searchParams.get(SEARCH_PARAM_KEYS.APP_STATE)

  const { setIsAppLoading } = useAppLoaderContext()
  const { removeStore, appConfig } = useConfigsContext()
  const handleLogout = useCallback(async () => {
    setIsAppLoading(true)
    const logoutUrl = await getLogoutUrl()
    router.push(logoutUrl)
  }, [router, setIsAppLoading])

  const { isSignedIn, isCheckingAuth, setIsSignedIn, checkAuth } = useCheckAuth({
    onLogout: handleLogout,
  })
  const vodafoneURLPostfix = appConfig?.features?.vodafone_url_postfix

  const handleLogin = useCallback(
    async ({ callback = '', loginParams }: LoginParams = {}) => {
      setIsAppLoading(true)

      // Get app_state from current URL and add it to loginParams
      const enhancedLoginParams = {
        ...loginParams,
        ...(appState && { app_state: appState }),
      }

      const loginUrl = await getLoginUrl({
        callback,
        loginParams: enhancedLoginParams,
        vodafoneURLPostfix,
      })
      router.push(loginUrl)
    },
    [router, setIsAppLoading, vodafoneURLPostfix, appState],
  )

  useEffect(() => {
    if (searchParams.get('debug') === 'true') {
      // TODO: just debug now.
      window._WEB_CONFIGS_ = appConfig
    }
  }, [searchParams, appConfig])

  useEffect(() => {
    if (hash === '#logout') {
      setIsAppLoading(true)
      setIsSignedIn(false)
      removeStore()

      const newURL = pathname + searchParams.toString()
      window.history.replaceState({}, document.title, newURL)

      setIsAppLoading(false)
    }
  }, [hash, pathname, removeStore, searchParams, setIsSignedIn, setIsAppLoading])

  useEffect(() => {
    if (!hasCheckedAuth) {
      setHasCheckedAuth(true)
      checkAuth()
    }
  }, [checkAuth, hasCheckedAuth])

  return (
    <AuthContext.Provider
      value={{
        isSignedIn,
        isCheckingAuth,
        handleLogin,
        handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthContext = () => {
  const value = useContext(AuthContext)

  if (value == null) {
    throw new Error('useAuthContext cannot be used outside of AuthProvider')
  }

  return value
}
